import React, { useEffect, useMemo, useState } from 'react';
import {
  Group,
  Text,
  Loader,
  Box,
  Flex,
  ActionIcon,
  Stack,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { IconLayoutGrid, IconX, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCompetencyMap } from '../../hooks/useCompetencyApi';
import { UserCompetency } from '../../types';

interface Props {
  onClose: () => void;
  mapId?: string;
  // Subject's acquired competencies — used to mark each row done / not done.
  competencies?: UserCompetency[];
  currentGradeId?: string;
  // Grade to show first (the badge that was clicked).
  focusGradeId?: string;
  subjectName?: string;
}

// Left column inside the review drawer: the subject's competencies for one grade
// at a time (grade switcher on top), grouped by swimlane, each marked acquired or
// not. A single focused grade reads far cleaner in a narrow panel than the full
// wide map table.
export const ReviewMapPanel: React.FC<Props> = ({
  onClose,
  mapId,
  competencies = [],
  currentGradeId,
  focusGradeId,
  subjectName,
}) => {
  const { t } = useTranslation();
  // Parent mounts this only while the panel is open, so gating on mapId is enough.
  const { data: map, isFetching } = useCompetencyMap(mapId ?? '');

  const grades = useMemo(() => map?.grades ?? [], [map]);
  const swimlanes = useMemo(() => map?.swimlanes ?? [], [map]);
  const acquired = useMemo(() => new Set(competencies.map((c) => c.competencyId)), [competencies]);

  const [selectedGradeId, setSelectedGradeId] = useState<string | undefined>();

  // Pick the grade to show once the map loads (or the clicked badge changes):
  // the clicked grade, else the subject's current grade, else the first grade.
  useEffect(() => {
    if (!grades.length) return;
    const has = (id?: string) => !!id && grades.some((g) => g._id === id);
    setSelectedGradeId(
      has(focusGradeId) ? focusGradeId : has(currentGradeId) ? currentGradeId : grades[0]._id,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map?._id, focusGradeId]);

  const selectedGrade = grades.find((g) => g._id === selectedGradeId);
  const comps = selectedGrade?.competencies ?? [];
  const acquiredCount = comps.filter((c) => acquired.has(c._id)).length;

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        gap="sm"
        px="md"
        py="sm"
        style={{
          flexShrink: 0,
          borderBottom: '1px solid var(--mantine-color-default-border)',
          background: 'var(--mantine-color-body)',
        }}
      >
        <Group gap="xs" style={{ minWidth: 0 }}>
          <IconLayoutGrid size={18} style={{ flexShrink: 0 }} />
          <Text fw={600} truncate>
            {t('perfReview.competenciesOf', { name: subjectName || '' })}
          </Text>
        </Group>
        <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label={t('common.close')}>
          <IconX size={16} />
        </ActionIcon>
      </Flex>

      {isFetching && !map ? (
        <Group justify="center" py="xl" style={{ flex: 1 }}>
          <Loader size="sm" />
        </Group>
      ) : !map || !grades.length ? (
        <Text c="dimmed" ta="center" py="xl" style={{ flex: 1 }}>
          {t('competencyMaps.mapNotFound')}
        </Text>
      ) : (
        <>
          {/* Grade switcher */}
          <Box px="md" py="sm" style={{ flexShrink: 0 }}>
            <Group
              gap={6}
              wrap="nowrap"
              className="no-scrollbar"
              style={{ overflowX: 'auto', paddingBottom: 4 }}
            >
              {grades.map((g) => {
                const isSelected = g._id === selectedGradeId;
                const isCurrent = g._id === currentGradeId;
                return (
                  <UnstyledButton
                    key={g._id}
                    onClick={() => setSelectedGradeId(g._id)}
                    style={{
                      flexShrink: 0,
                      padding: '5px 12px',
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: isSelected ? 700 : 500,
                      whiteSpace: 'nowrap',
                      color: isSelected
                        ? 'var(--mantine-color-white)'
                        : 'var(--mantine-color-text)',
                      background: isSelected ? 'var(--mantine-color-teal-filled)' : 'transparent',
                      border: `1px solid ${
                        isSelected || isCurrent
                          ? 'var(--mantine-color-teal-filled)'
                          : 'var(--mantine-color-default-border)'
                      }`,
                    }}
                  >
                    {g.title}
                    {isCurrent && !isSelected && (
                      <Text component="span" c="teal" fw={700} inherit>
                        {' '}
                        ·
                      </Text>
                    )}
                  </UnstyledButton>
                );
              })}
            </Group>
            <Text size="xs" c="dimmed" mt={8}>
              {t('perfReview.acquiredOfTotal', { count: acquiredCount, total: comps.length })}
            </Text>
          </Box>

          {/* Competencies of the selected grade, grouped by swimlane */}
          <Box style={{ flex: 1, overflowY: 'auto', padding: '4px 12px 14px' }}>
            <Stack gap="lg">
              {swimlanes.map((sw) => {
                const rows = comps.filter((c) => c.swimlaneId === sw._id);
                if (rows.length === 0) return null;
                return (
                  <div key={sw._id}>
                    <Text
                      size="xs"
                      fw={600}
                      c="dimmed"
                      mb={8}
                      style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
                    >
                      {sw.title}
                    </Text>
                    <Stack gap={6}>
                      {rows.map((c) => {
                        const has = acquired.has(c._id);
                        return (
                          <Group
                            key={c._id}
                            gap={10}
                            wrap="nowrap"
                            align="flex-start"
                            style={{
                              padding: '8px 10px',
                              borderRadius: 8,
                              border: '1px solid var(--mantine-color-default-border)',
                              background: has ? 'var(--mantine-color-teal-light)' : 'transparent',
                            }}
                          >
                            {has ? (
                              <ThemeIcon
                                size={20}
                                radius="xl"
                                color="teal"
                                variant="filled"
                                style={{ flexShrink: 0, marginTop: 1 }}
                              >
                                <IconCheck size={12} />
                              </ThemeIcon>
                            ) : (
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 999,
                                  flexShrink: 0,
                                  marginTop: 1,
                                  border: '1.5px solid var(--mantine-color-default-border)',
                                }}
                              />
                            )}
                            <Text
                              size="sm"
                              c={has ? undefined : 'dimmed'}
                              fw={has ? 500 : 400}
                              style={{ minWidth: 0 }}
                            >
                              {c.title}
                            </Text>
                          </Group>
                        );
                      })}
                    </Stack>
                  </div>
                );
              })}
            </Stack>
          </Box>
        </>
      )}
    </>
  );
};
